import { ref, computed, watch, nextTick } from "@vue/composition-api";
import { debounce } from "lodash";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import router from "@/router";
import ability from "@/libs/acl/ability";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import axios from "@axios";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { fetchOperationalFormTemplateOptions } from "@/composables/useOperationalFormTemplateOptions";
import {
  BAU_COMPLEMENTS_QUERY_KEY,
  BAU_COMPLEMENTS_UNIT_QUERY_VALUE,
  hasBauComplementsQueryFlag,
  shouldSendBauAnswersVisitFilter,
} from "@/utils/bauFormQuery";

const BAU_COMPLEMENT_TEMPLATE_NAME = "Visitas Inmuebles";

const normalizeUserText = (value) => String(value || "").trim();

const buildUserFilterOption = (userOption) => {
  const fullName = normalizeUserText(userOption?.full_name || userOption?.name);
  const user =
    normalizeUserText(userOption?.user) ||
    normalizeUserText(userOption?.username) ||
    normalizeUserText(userOption?.email);
  const value = userOption?.id;
  if (value == null) return null;
  const label = fullName && user ? `${fullName} | ${user}` : fullName || user;
  return {
    value,
    label: label || String(value),
  };
};

const getTemplateIdentifierByName = ({ templateOptions, templateName }) => {
  const template = templateOptions.find(({ name }) => name === templateName);
  if (!template) {
    return null;
  }

  return template.id;
};

export default function useList(props) {
  const toast = useToast();
  const { t } = useI18nUtils();
  const refListTable = ref(null);
  const currentEvaluationId = ref(null);
  const currentEvaluation = ref(null);
  const isCreateRegistration = ref(null);
  const showEvaluationForm = ref(false);
  const showFormEmailPreview = ref(false);
  const isCreateSpecialForms = ref(false);
  const idFormAnswerOwner = ref(null);
  const isFilterPanelOpen = ref(false);
  const currentSpecialForm = ref(false);
  const currentDataToReplace = ref(null);
  const openFromUrlParam = ref(false);
  const currentUrlParam = ref(null);
  const isNavigatingBack = ref(false); // Flag para evitar agregar al historial durante back
  const translations = {
    "operational_security.evaluation.error_send_form": t(
      "operational_security.evaluation.error_send_form"
    ),
    "operational_security.evaluation.success_send_form": t(
      "operational_security.evaluation.success_send_form"
    ),
  };

  // Computed
  const hasNavigationHistory = computed(() =>
    store.getters["operational_security/hasNavigationHistory"](props.slotId || "main")
  );

  store.dispatch("user/getAll", { withoutConfig: true });
  store.dispatch("unit/getAll", { with: "router:id,id_tenant" });

  const showFilters = computed(() =>
    props.showFilters !== undefined ? props.showFilters : true
  );

  // store.dispatch('tenant/getAll')

  const templateOptions = ref([]);
  const templateFilter = ref(null);
  // For BAU Form
  const isBauComplementsMode = hasBauComplementsQueryFlag(
    router.currentRoute?.query || {}
  );
  const hasAppliedBauComplementsTemplateFilter = ref(false);
  const unitFilter = ref(null);
  const sirhFilter = ref(null);
  const remsFilter = ref(null);
  const sectorFilter = ref(null);
  const regionFilter = ref(null);
  const userFilter = ref(null);
  const statusFilter = ref(null);
  const tenantOptions = computed(() => store.getters["tenant/getSelectedItems"]);
  const isCloseOperatinalSecurityFormModal = computed(
    () => store.getters["grid/isCloseOperatinalSecurityFormModal"]
  );
  const tenantFilter = ref(null);
  const fromFilter = ref(null);
  const toFilter = ref(null);

  if (router.currentRoute.params.id_tenant)
    tenantFilter.value = parseInt(router.currentRoute.id_tenant);

  const tableColumns = [
    { key: "actions", label: "actions.name" },
    { label: "security_self_audit.reference_number", key: "ref", sortable: false },
    ...(isBauComplementsMode
      ? [{ label: "bau_complement.property", key: "name_unit", sortable: false }]
      : []),
    ...(isBauComplementsMode
      ? []
      : [{ label: "risk.template.title", key: "id_template", sortable: false }]),
    ...(isBauComplementsMode
      ? []
      : [
          {
            label: "operational_security.evaluation.related_forms",
            key: "related_forms",
            sortable: false,
          },
        ]),
    // { label: 'unit.title', key: 'id_unit', sortable: false },
    { label: "users.title", key: "user", sortable: false },
    {
      label: "operational_security.evaluation.form_start_at",
      key: "form_start_at",
      sortable: false,
    },
    {
      label: "operational_security.evaluation.form_end_at",
      key: "form_end_at",
      sortable: false,
    },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const MAIL_FORMS = new Set([
    "SITREP",
    "Boletín Informativo",
    "Flash Informativo",
    "Correo Informativo",
  ]);
  const user = store.getters["user/getUser"];

  // Options for filters

  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);

  const userOptions = computed(() => {
    const users = store.getters["user/getItems"] || [];
    return users.map((option) => buildUserFilterOption(option)).filter(Boolean);
  });

  const fetchUsers = async (query = "") => {
    await store.dispatch("user/getAll", { max: 10, q: query, withoutConfig: true });
  };

  const statusOptions = computed(() => {
    const statuses = [
      { value: 1, key: "in_progress", variant: "primary" },
      { value: 2, key: "pending", variant: "warning" },
      { value: 3, key: "review", variant: "info" },
      { value: 4, key: "completed", variant: "success" },
      { value: 5, key: "rejected", variant: "danger" },
      { value: 6, key: "reopened", variant: "primary" },
    ];
    const allowedBauStatusKeys = new Set(["in_progress", "completed"]);
    const filteredStatuses = isBauComplementsMode
      ? statuses.filter(({ key }) => allowedBauStatusKeys.has(key))
      : statuses;

    return filteredStatuses.map((status) => ({
      value: status.value,
      label: t(`operational_security.evaluation.status.${status.key}`),
    }));
  });

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const relatedTemplates = (item) => {
    return item?.template?.related_templates || []
  };

  const validateIfUrlHasParams = () => {
    const templateId = getQueryParam("template");
    const editId = getQueryParam("edit");
    if (templateId || editId) {
      currentUrlParam.value = templateId
        ? { name: "template", value: templateId }
        : { name: "edit", value: editId };
      openFromUrlParam.value = true;
      openEvaluationModal();
      setTimeout(() => {
        openFromUrlParam.value = false;
      }, 2000);
      return;
    }
  };

  function getQueryParam(param) {
    const queryString = window.location.hash.split("?")[1]; // Extract the query string after `#`
    if (!queryString) return null;

    const params = new URLSearchParams(queryString);
    return params.get(param);
  }

  const fetchList = (ctx, callback) => {
    axios
      .get("/v1/operational-security-forms/template-answers", {
        params: {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          templates: templateFilter.value,
          facility: unitFilter.value,
          sirh: sirhFilter.value,
          rems: remsFilter.value,
          user: userFilter.value,
          status: statusFilter.value,
          completedForms: true,
          from: fromFilter.value,
          to: toFilter.value,
          ...(isBauComplementsMode
            ? {
                bauForm: true,
                ...(sectorFilter.value != null && sectorFilter.value !== ""
                  ? { sector_id: sectorFilter.value }
                  : {}),
                ...(regionFilter.value != null && regionFilter.value !== ""
                  ? { region_id: regionFilter.value }
                  : {}),
              }
            : { excludeBauComplements: true }),
          ...(shouldSendBauAnswersVisitFilter(router.currentRoute)
            ? { bau_answers_visit: true }
            : {}),
        },
      })
      .then((response) => {
        callback(response.data.data);
        totalList.value = response.data.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatData = (data) => {
    const isUserSupervisor = isSupervisor(user.profile.type);
    return data.map((item) => ({
      ...item,
      template: {
        ...item.template,
        related_templates: isUserSupervisor
          ? item.template.related_templates
          : item.template.related_templates.filter(
              (t) => !MAIL_FORMS.has(t.name?.trim())
            ),
      },
    }));
  };

  function isSupervisor(profile) {
    const p = profile.toLowerCase();
    return p.includes("supervisor") || p.includes("masteradmin");
  }

  const refetchData = () => {
    if (refListTable.value) {
      refListTable.value.refresh();
    }
  };

  const resetToFirstPage = () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }

    refetchData();
  };

  watch(currentPage, refetchData);
  watch(perPage, resetToFirstPage);
  watch(searchQuery, debounce(resetToFirstPage, 500));

  watch(isCloseOperatinalSecurityFormModal, () => {
    showEvaluationForm.value = false;
    currentEvaluationId.value = null;
    currentEvaluation.value = null;
    isCreateRegistration.value = null;
    nextTick(() => {
      refetchData();
    });
  });

  watch(
    [
      templateFilter,
      unitFilter,
      sirhFilter,
      remsFilter,
      sectorFilter,
      regionFilter,
      userFilter,
      statusFilter,
      fromFilter,
      toFilter,
    ],
    () => {
      if (!showFilters.value) {
        buildFilters();
      }
    }
  );

  const filters = ref({});

  const filtersCount = useFilterCounter(filters);

  const getBauComplementsTemplateId = () =>
    getTemplateIdentifierByName({
      templateOptions: templateOptions.value,
      templateName: BAU_COMPLEMENT_TEMPLATE_NAME,
    });

  const buildFilters = () => {
    filters.value = {
      id_template: templateFilter.value,
      id_unit: unitFilter.value,
      sirh: sirhFilter.value,
      rems: remsFilter.value,
      sector_id: sectorFilter.value,
      region_id: regionFilter.value,
      user: userFilter.value,
      status: statusFilter.value,
      from: fromFilter.value,
      to: toFilter.value,
    };

    resetToFirstPage();
  };

  const applyBauComplementsTemplateFilter = () => {
    if (!isBauComplementsMode) {
      return;
    }

    if (hasAppliedBauComplementsTemplateFilter.value) {
      return;
    }

    const bauComplementsTemplateId = getBauComplementsTemplateId();
    if (!bauComplementsTemplateId) {
      return;
    }

    templateFilter.value = bauComplementsTemplateId;
    hasAppliedBauComplementsTemplateFilter.value = true;
    buildFilters();
  };

  const clearSearchFilters = () => {
    if (isBauComplementsMode) {
      templateFilter.value = getBauComplementsTemplateId();
    } else {
      templateFilter.value = null;
    }
    unitFilter.value = null;
    sirhFilter.value = null;
    remsFilter.value = null;
    sectorFilter.value = null;
    regionFilter.value = null;
    userFilter.value = null;
    statusFilter.value = null;
    fromFilter.value = null;
    toFilter.value = null;
    isFilterPanelOpen.value = false;
    filters.value = {};
    resetToFirstPage();
  };

  const updatePagination = (data) => {
    currentPage.value = data;
  };

  const openEvaluationModal = () => {
    const slotId = props.slotId || "main";
    store.dispatch("operational_security/clearHistory", slotId);
    currentEvaluationId.value = null;
    currentEvaluation.value = null;
    isCreateRegistration.value = true;
    showEvaluationForm.value = true;
  };

  const openEditEvaluationModal = (data) => {
    currentEvaluationId.value = data.id;
    currentEvaluation.value = data;
    isCreateRegistration.value = false;
    showEvaluationForm.value = true;
  };

  const openFormEmailPreview = (data) => {
    currentEvaluationId.value = data.id;
    currentEvaluation.value = data;
    showFormEmailPreview.value = true;
  };

  const openCreateNewFormType = (data, type, isFromForm = false) => {
    if (isFromForm) goBackToList();
    if (type?.name === BAU_COMPLEMENT_TEMPLATE_NAME) {
      const currentQuery = { ...(router.currentRoute?.query || {}) };
      const nextQuery = {
        ...currentQuery,
        [BAU_COMPLEMENTS_QUERY_KEY]: BAU_COMPLEMENTS_UNIT_QUERY_VALUE,
      };
      router.replace({ query: nextQuery }).catch(() => {});
    }
    setTimeout(() => {
      currentSpecialForm.value = type.name;
      isCreateSpecialForms.value = true;
      idFormAnswerOwner.value = data.id;
      showEvaluationForm.value = true;
      currentDataToReplace.value = data?.externalFields;
    }, 150);
  };

  const isInProgressStatus = (item) => {
    const statusRaw = item?.status;
    if (statusRaw === null || statusRaw === undefined) return true;
    const normalizedStatus = String(statusRaw).toLowerCase().trim();
    if (normalizedStatus === "in_progress" || normalizedStatus === "1") return true;
    const statusNameRaw = item?.status_name;
    const normalizedStatusName = statusNameRaw ? String(statusNameRaw).toLowerCase().trim() : "";
    return normalizedStatusName === "in_progress";
  };

  const deleteBauComplementFromRow = async (item) => {
    const bauComplementId = item?.id;
    const hasBauComplementsQuery = hasBauComplementsQueryFlag(router.currentRoute?.query || {});
    const isInProgress = isInProgressStatus(item);
    if (!bauComplementId || !hasBauComplementsQuery || !isInProgress) return;

    try {
      await axios.delete(`/v1/operational-security-forms/template-answers/${bauComplementId}`);
      toast({
        component: ToastificationContent,
        props: {
          title: "Registro eliminado correctamente",
          icon: "CheckIcon",
          variant: "success",
        },
      });
      refetchData();
    } catch (_) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Error al eliminar el registro",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    }
  };

  const sendForm = (data) => {
    axios
      .post(`/v1/operational-security-forms/template-answers/emails/send/${data.id}`)
      .then((response) => {
        toast({
          component: ToastificationContent,
          props: {
            title: translations["operational_security.evaluation.success_send_form"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: translations["operational_security.evaluation.error_send_form"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const goBackToList = async () => {
    const slotId = props?.slotId || null;
    const hasHistory = store.getters["operational_security/hasNavigationHistory"](slotId);

    if (hasHistory) {
      const previousForm = await store.dispatch(
        "operational_security/navigateBack",
        slotId
      );

      if (previousForm) {
        try {
          isNavigatingBack.value = true; // Activar bandera ANTES de cargar

          const response = await axios.get(
            `/v1/operational-security-forms/template-answers/${previousForm.id}`
          );
          const formData = response.data;

          await nextTick();

          currentEvaluationId.value = formData.id;
          currentEvaluation.value = formData;
          isCreateRegistration.value = false;
          showEvaluationForm.value = true;

          // Desactivar bandera después de un tick
          await nextTick();
          isNavigatingBack.value = false;

          return;
        } catch (error) {
          isNavigatingBack.value = false; // Desactivar en caso de error
          toast({
            component: ToastificationContent,
            props: {
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      }
    }

    store.dispatch("operational_security/clearHistory", slotId);
    showEvaluationForm.value = false;
    currentEvaluationId.value = null;
    currentEvaluation.value = null;
    isCreateRegistration.value = false;
    showFormEmailPreview.value = false;
    isCreateSpecialForms.value = false;
    idFormAnswerOwner.value = null;
    currentSpecialForm.value = null;
    currentDataToReplace.value = null;
  };

  const handleNavigateToForm = async (formId) => {
    if (isNavigatingBack.value) {
      return;
    }

    try {
      const slotId = props.slotId || "main";

      if (currentEvaluationId.value) {
        await store.dispatch("operational_security/navigateToForm", {
          slotId: slotId,
          formData: {
            id: currentEvaluationId.value,
            data: currentEvaluation.value,
          },
        });
      }

      const response = await axios.get(
        `/v1/operational-security-forms/template-answers/${formId}`
      );
      const formData = response.data;

      await nextTick();

      currentEvaluationId.value = formData.id;
      currentEvaluation.value = formData;
      isCreateRegistration.value = false;

      showEvaluationForm.value = true;
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    }
  };

  const applyFilters = () => {
    buildFilters();
    isFilterPanelOpen.value = false;
  };

  const fetchListTemplates = () => {
    axios
      .get("/v1/operational-security-forms/templates")
      .then((response) => {
        const { data } = response.data;

        templateOptions.value = isBauComplementsMode
          ? data
          : data.filter(({ name }) => name !== BAU_COMPLEMENT_TEMPLATE_NAME);
        applyBauComplementsTemplateFilter();
      })
      .catch(() => {});
  };

  fetchListTemplates();

  const formTemplateOptionsForAssignTask = ref([]);

  const fetchFormTemplatesForAssignTask = () => {
    fetchOperationalFormTemplateOptions()
      .then((options) => {
        formTemplateOptionsForAssignTask.value = options;
      })
      .catch(() => {
        formTemplateOptionsForAssignTask.value = [];
      });
  };

  fetchFormTemplatesForAssignTask();

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    templateOptions,
    templateFilter,
    filtersCount,
    currentEvaluationId,
    currentEvaluation,
    isCreateRegistration,
    showEvaluationForm,
    showFormEmailPreview,
    isCreateSpecialForms,
    idFormAnswerOwner,
    unitOptions,
    unitFilter,
    sirhFilter,
    remsFilter,
    sectorFilter,
    regionFilter,
    userOptions,
    fetchUsers,
    userFilter,
    fromFilter,
    toFilter,
    statusOptions,
    statusFilter,
    isFilterPanelOpen,
    currentSpecialForm,
    currentDataToReplace,
    hasNavigationHistory,
    openFromUrlParam,
    currentUrlParam,
    relatedTemplates,
    fetchList,
    refetchData,
    buildFilters,
    clearSearchFilters,
    updatePagination,
    openEvaluationModal,
    openEditEvaluationModal,
    goBackToList,
    openFormEmailPreview,
    openCreateNewFormType,
    isInProgressStatus,
    deleteBauComplementFromRow,
    sendForm,
    applyFilters,
    handleNavigateToForm,
    validateIfUrlHasParams,
    formTemplateOptionsForAssignTask,
  };
}
